import { type FC } from 'react';

import { SideNavigationGroup, SideNavigationItem, Icon } from '@livechat/design-system-react-components';
import memoizeOne from 'memoize-one';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import { AppsEvent, AppsSection } from 'constants/apps/events';
import { navigateToMarketplace, preloadMarketplacePage } from 'helpers/routing';
import { trackAppsSectionEvent } from 'services/event-tracking';
import { getCanManageWebhooks } from 'store/features/session/selectors';
import { AppsViewActions } from 'store/views/apps/actions';
import { getCurrentMarketplaceRoute } from 'store/views/apps/selectors';

import { checkIfActive } from '../helpers';

import { ADDITIONAL_SECTIONS_LINKS, MARKETPLACE_SECTIONS_LINKS, WEBHOOKS_LINK } from './constants';

export const AdditionalSectionsList: FC = () => {
  const currentMarketplaceRoute = useSelector(getCurrentMarketplaceRoute);
  const canManageWebhooks = useSelector(getCanManageWebhooks);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const handleMarketplaceSectionClick = (link: string, label: string): void => {
    navigateToMarketplace(link);
    dispatch(AppsViewActions.setMarketplaceRoute(link));
    trackAppsSectionEvent(AppsEvent.SectionItemClicked, AppsSection.SideNavigation, { label });
  };

  const handleAdditionalSectionClick = (link: string, label: string): void => {
    navigate(link);
    trackAppsSectionEvent(AppsEvent.SectionItemClicked, AppsSection.SideNavigation, {
      label,
    });
  };
  const handleHoverMarketplaceItem = (link: string): void => preloadMarketplacePage(link);

  const itemsWithWebhooksLink = memoizeOne((canManageWebhooks) => [
    ...ADDITIONAL_SECTIONS_LINKS,
    ...(canManageWebhooks ? [WEBHOOKS_LINK] : []),
  ]);

  return (
    <SideNavigationGroup>
      {MARKETPLACE_SECTIONS_LINKS.map(({ label, link, icon }) => (
        <SideNavigationItem
          isActive={checkIfActive(link, currentMarketplaceRoute)}
          key={link}
          url={link}
          label={label}
          onClick={() => handleMarketplaceSectionClick(link, label)}
          onItemHover={() => handleHoverMarketplaceItem(link)}
          leftNode={<Icon source={icon} />}
        />
      ))}
      {itemsWithWebhooksLink(canManageWebhooks).map(({ label, link, icon }) => {
        const isActive = Boolean(matchPath({ path: link }, pathname));

        return (
          <SideNavigationItem
            isActive={isActive}
            key={link}
            url={link}
            label={label}
            onClick={() => handleAdditionalSectionClick(link, label)}
            leftNode={<Icon source={icon} />}
          />
        );
      })}
    </SideNavigationGroup>
  );
};
